import * as React from "react";
import get from "lodash/get";
import uniqBy from "lodash/uniqBy";

import "./ReferralsHistory.css";
import { isEditMode } from "../../utils/sendClickToMerchant";
import { getIsLoadingShares, getShares } from "../../reducers/share/methods";
import { resendShare } from "../../actions";
import { Share } from "../../reducers/share/shares";
import { ComponentBase, ComponentBaseProps } from "../ComponentBase";

export interface ReferralsHistoryProps extends ComponentBaseProps {
  classNames: string;
  name: string;
  configName: string;
  style?: React.CSSProperties;
  borderStyle?: React.CSSProperties;
  cellStyle?: React.CSSProperties;
  emailStyle?: React.CSSProperties;
  dateStyle?: React.CSSProperties;
  completedStatusStyle?: React.CSSProperties;
  invitedStatusStyle?: React.CSSProperties;
  notAuthenticatedStyle?: React.CSSProperties;
  notAuthenticatedText?: string;
  noSharesText?: string;
  noSharesStyle?: React.CSSProperties;
  pendingStatusStyle?: React.CSSProperties;
  rejectedStatusStyle?: React.CSSProperties;
  resendStyle?: React.CSSProperties;
  rowStyle?: React.CSSProperties;
  statusStyle?: React.CSSProperties;
  tableStyle?: React.CSSProperties;
  align?: "flex-start" | "flex-end" | "center" | "baseline" | "stretch";
  resendShare: typeof resendShare;
  isLoading: ReturnType<typeof getIsLoadingShares>;
  shares: ReturnType<typeof getShares>;
  testMode?: string;
}

// trying to get the version cache to show an update
export class ReferralsHistory extends ComponentBase<ReferralsHistoryProps> {
  renderNoShares = () => {
    return (
      <div style={this.props.noSharesStyle}>
        {this.props.noSharesText || ""}
      </div>
    );
  };

  renderNotAuthenticated = () => {
    return (
      <div style={this.props.notAuthenticatedStyle}>
        {this.props.notAuthenticatedText || ""}
      </div>
    );
  };

  renderReferralsHistory = (shares: Share[]) => {
    const {
      borderStyle,
      cellStyle,
      classNames,
      emailStyle,
      dateStyle,
      completedStatusStyle,
      invitedStatusStyle,
      pendingStatusStyle,
      rejectedStatusStyle,
      resendStyle,
      resendShare,
      rowStyle,
      statusStyle,
      tableStyle,
    } = this.props;

    return (
      <table
        className={`ReferralsHistory-container ${classNames || ""}`}
        style={{
          ...tableStyle,
          ...borderStyle,
        }}
        aria-label="Referrals History"
        cellPadding={0}
        cellSpacing={0}
      >
        <tbody>
          {shares.map((share) => {
            return (
              <tr
                key={`${share.recipient}-${share.shareId}`}
                style={{ ...borderStyle, ...rowStyle }}
              >
                <td
                  className="ReferralsHistory-cell"
                  style={{ ...dateStyle, ...cellStyle, ...borderStyle }}
                >
                  <div>{new Date(share.sharedOn).toLocaleDateString()}</div>
                </td>
                <td
                  className="ReferralsHistory-cell"
                  style={{ ...emailStyle, ...cellStyle, ...borderStyle }}
                >
                  <div className="ReferralsHistory-email-cell">
                    {share.recipient}
                  </div>
                </td>
                {share.status === "approved" && (
                  <td
                    className="ReferralsHistory-cell"
                    style={{
                      ...statusStyle,
                      ...completedStatusStyle,
                      ...cellStyle,
                      ...borderStyle,
                    }}
                  >
                    <div>Completed</div>
                  </td>
                )}
                {share.status === "invited" && (
                  <td
                    className="ReferralsHistory-cell"
                    style={{ ...statusStyle, ...cellStyle, ...borderStyle }}
                  >
                    <div className="ReferralsHistory-status-cell">
                      <div style={invitedStatusStyle}>Invited</div>
                      {share.resentStatus === false && (
                        <ResendShare
                          resendShare={resendShare}
                          resendStyle={resendStyle}
                          share={share}
                        />
                      )}
                    </div>
                  </td>
                )}
                {share.status === "pending" && (
                  <td
                    className="ReferralsHistory-cell"
                    style={{
                      ...statusStyle,
                      ...pendingStatusStyle,
                      ...cellStyle,
                      ...borderStyle,
                    }}
                  >
                    <div>Pending</div>
                  </td>
                )}
                {share.status === "rejected" && (
                  <td
                    className="ReferralsHistory-cell"
                    style={{
                      ...statusStyle,
                      ...rejectedStatusStyle,
                      ...cellStyle,
                      ...borderStyle,
                    }}
                  >
                    <div>Cancelled</div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  // tslint:disable-next-line: cyclomatic-complexity
  render() {
    const { data, isLoading, testMode, isEditing, style } = this.props;

    const shares = isEditMode()
      ? exampleShares
      : uniqBy(this.props.shares || [], (share) => share.recipient);

    const isCustomerAuthenticated = get(data, "customer.authenticated", false);

    if (isEditing) {
      return (
        <div
          style={{ ...style }}
          id={this.props.configName}
          onDragStart={this.onDragStart}
          onMouseDown={this.onMouseDown}
          ref={(component) => (this.component = component)}
          className={`ReferralsHistory-div-container`}
        >
          {testMode === "notAuthenticated" && this.renderNotAuthenticated()}
          {testMode === "noShares" && this.renderNoShares()}
          {(testMode === "shares" || !testMode) &&
            this.renderReferralsHistory(shares)}
        </div>
      );
    }

    if (isLoading) {
      return null;
    }

    return (
      <div
        style={{ ...style }}
        id={this.props.configName}
        onDragStart={this.onDragStart}
        onMouseDown={this.onMouseDown}
        ref={(component) => (this.component = component)}
        className={`ReferralsHistory-div-container`}
      >
        {!isCustomerAuthenticated && this.renderNotAuthenticated()}
        {isCustomerAuthenticated &&
          shares.length === 0 &&
          this.renderNoShares()}
        {isCustomerAuthenticated &&
          shares.length > 0 &&
          this.renderReferralsHistory(shares)}
      </div>
    );
  }
}

interface ResendShareProps {
  resendShare: typeof resendShare;
  resendStyle?: React.CSSProperties;
  share: Share;
}

class ResendShare extends React.PureComponent<ResendShareProps> {
  resendShare = () => {
    this.props.resendShare({
      recipient: this.props.share.recipient,
      shareId: this.props.share.shareId,
    });
  };

  render() {
    return (
      <div
        tabIndex={0}
        style={{ ...this.props.resendStyle, cursor: "pointer" }}
        onClick={this.resendShare}
      >
        Resend
      </div>
    );
  }
}

const exampleShares: Share[] = [
  {
    channel: "email",
    recipient: "lily_kdledin@yahoo.com",
    shareId: "1",
    sharedOn: "May 13 2019",
    status: "approved",
    resentStatus: false,
  },
  {
    channel: "email",
    recipient: "aa3ron@gmail.com",
    shareId: "2",
    sharedOn: "May 13 2019",
    status: "pending",
    resentStatus: false,
  },
  {
    channel: "email",
    recipient: "lindsay191@friendbuy.com",
    shareId: "3",
    sharedOn: "May 13 2019",
    status: "invited",
    resentStatus: false,
  },
  {
    channel: "email",
    recipient: "georgie@yahoo.com",
    shareId: "4",
    sharedOn: "May 11 2019",
    status: "invited",
    resentStatus: true,
  },
  {
    channel: "email",
    recipient: "doug@freshmail.com",
    shareId: "5",
    sharedOn: "May 10 2019",
    status: "approved",
    resentStatus: false,
  },
  {
    channel: "email",
    recipient: "mom@gmail.com",
    shareId: "6",
    sharedOn: "May 10 2019",
    status: "rejected",
    resentStatus: false,
  },
];
